import { useCallback, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { cancelAppointment } from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import useNotify from '../../../hooks/useNotify';
import './CancelAppointmentModal.scss';

interface CancelAppointmentModalProps {
  appointmentId: number;
  patientName: string;
  visible: boolean;
  onHide: () => void;
  onCancelled: () => void;
  /** When 'reschedule', the modal auto-triggers onReschedule after successful cancellation */
  mode?: 'cancel' | 'reschedule';
  onReschedule?: () => void;
}

export default function CancelAppointmentModal({
  appointmentId,
  patientName,
  visible,
  onHide,
  onCancelled,
  mode = 'cancel',
  onReschedule,
}: CancelAppointmentModalProps) {
  const notify = useNotify();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = useCallback(() => {
    setReason('');
    onHide();
  }, [onHide]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      await cancelAppointment(appointmentId, reason);
      onCancelled();
      resetAndClose();
      if (mode === 'reschedule') {
        onReschedule?.();
      }
    } catch (err) {
      // Error general: card flotante; el modal queda abierto con el motivo cargado para reintentar
      notify.error(parseApiError(err, 'No se pudo cancelar el turno. Intentá de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  }, [appointmentId, reason, mode, onCancelled, onReschedule, resetAndClose, notify]);

  const title = mode === 'reschedule' ? 'Cambiar turno' : 'Cancelar turno';
  const confirmLabel = mode === 'reschedule' ? 'Cancelar y reprogramar' : 'Cancelar turno';
  const patient = <strong className="nv-strong">{patientName || 'este paciente'}</strong>;

  const footer = (
    <>
      <Button type="button" className="buttonMain2" outlined label="Volver" onClick={resetAndClose} />
      <Button type="button" className="buttonMain" label={confirmLabel} onClick={handleConfirm} loading={submitting} />
    </>
  );

  return (
    <Dialog
      visible={visible}
      onHide={resetAndClose}
      header={title}
      footer={footer}
      modal
      className="nv-scope nv-dialog nv-dialog-s"
      style={{ width: '460px' }}
      closable
      draggable={false}
    >
      <div className="nv-dialog-body">
        <p className="cancel-modal__message">
          {mode === 'reschedule'
            ? <>Se va a cancelar el turno actual de {patient} y se va a abrir la pantalla para elegir un nuevo horario.</>
            : <>Se va a cancelar el turno de {patient}. El horario queda disponible para otro paciente.</>
          }
        </p>
        <div className="nv-field">
          <label htmlFor="cancel-appointment-reason">Motivo</label>
          <InputTextarea
            id="cancel-appointment-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Opcional"
          />
        </div>
      </div>
    </Dialog>
  );
}
